// ╔══════════════════════════════════════════════════════════════════╗
// ║                    ADMIN SETTINGS PAGE                           ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Settings,
  Save,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle,
  Bell,
  Shield,
  Mail,
  Globe,
  Database,
  Clock,
  DollarSign,
  Percent,
  ToggleLeft,
  ToggleRight,
  Plus,
  Edit2,
  Trash2,
  X,
  Megaphone,
  Calendar
} from 'lucide-react'
import { SettingsAdminAPI } from '../../services/adminApi'
import { useAdmin } from '../AdminApp'

export default function SettingsPage() {
  const { hasPermission } = useAdmin()
  const [activeTab, setActiveTab] = useState('general')

  // Settings state
  const [settings, setSettings] = useState({})
  const [loadingSettings, setLoadingSettings] = useState(true)

  // Announcements state
  const [announcements, setAnnouncements] = useState([])
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false)

  // Common state
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  // Modal
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState(null)

  useEffect(() => {
    if (activeTab === 'announcements') {
      fetchAnnouncements()
    } else {
      fetchSettings()
    }
  }, [activeTab])

  const fetchSettings = async () => {
    setLoadingSettings(true)
    setError(null)

    try {
      const res = await SettingsAdminAPI.getSettings()
      if (res.success) {
        // Convert array to object for easier manipulation
        const settingsObj = {}
        res.data.settings.forEach(s => {
          settingsObj[s.key] = {
            ...s,
            value: s.type === 'boolean' ? s.value === 'true' : s.value
          }
        })
        setSettings(settingsObj)
      } else {
        setError(res.message)
      }
    } catch (err) {
      setError('Failed to load settings')
    } finally {
      setLoadingSettings(false)
    }
  }

  const fetchAnnouncements = async () => {
    setLoadingAnnouncements(true)
    setError(null)

    try {
      const res = await SettingsAdminAPI.getAnnouncements()
      if (res.success) {
        setAnnouncements(res.data.announcements)
      } else {
        setError(res.message)
      }
    } catch (err) {
      setError('Failed to load announcements')
    } finally {
      setLoadingAnnouncements(false)
    }
  }

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: { ...prev[key], value }
    }))
    setSaved(false)
  }

  const handleSaveSettings = async () => {
    setSaving(true)
    setError(null)

    try {
      // Convert settings object to array of updates
      const updates = Object.entries(settings).map(([key, setting]) => ({
        key,
        value: String(setting.value)
      }))

      const res = await SettingsAdminAPI.bulkUpdateSettings(updates)

      if (res.success) {
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      } else {
        setError(res.message)
      }
    } catch (err) {
      setError('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleSaveAnnouncement = async (announcementData) => {
    setSaving(true)
    try {
      const res = await SettingsAdminAPI.saveAnnouncement(announcementData)
      if (res.success) {
        fetchAnnouncements()
        setShowAnnouncementModal(false)
        setEditingAnnouncement(null)
      }
    } catch (err) {
      console.error('Failed to save announcement')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return

    try {
      const res = await SettingsAdminAPI.deleteAnnouncement(announcementId)
      if (res.success) {
        fetchAnnouncements()
      }
    } catch (err) {
      console.error('Failed to delete announcement')
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  // Group settings by category
  const settingCategories = {
    general: {
      icon: Globe,
      label: 'General',
      keys: ['site_name', 'support_email', 'support_phone', 'maintenance_mode']
    },
    security: {
      icon: Shield,
      label: 'Security',
      keys: ['session_timeout', 'max_login_attempts', 'require_2fa', 'password_expiry_days']
    },
    transactions: {
      icon: DollarSign,
      label: 'Transactions',
      keys: ['daily_transfer_limit', 'monthly_transfer_limit', 'min_transfer_amount', 'max_transfer_amount']
    },
    notifications: {
      icon: Bell,
      label: 'Notifications',
      keys: ['email_notifications', 'sms_notifications', 'push_notifications']
    }
  }

  const renderSettingInput = (setting) => {
    if (!setting) return null

    switch (setting.type) {
      case 'boolean':
        return (
          <button
            onClick={() => handleSettingChange(setting.key, !setting.value)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              setting.value ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-400'
            }`}
          >
            {setting.value ? (
              <ToggleRight className="w-5 h-5" />
            ) : (
              <ToggleLeft className="w-5 h-5" />
            )}
            {setting.value ? 'Enabled' : 'Disabled'}
          </button>
        )
      case 'number':
        return (
          <input
            type="number"
            value={setting.value}
            onChange={(e) => handleSettingChange(setting.key, e.target.value)}
            className="w-48 px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          />
        )
      default:
        return (
          <input
            type="text"
            value={setting.value}
            onChange={(e) => handleSettingChange(setting.key, e.target.value)}
            className="w-full max-w-md px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          />
        )
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-slate-400 mt-1">Configure system settings and preferences</p>
        </div>
        {activeTab !== 'announcements' && hasPermission('settings.edit') && (
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : saved ? (
              <CheckCircle className="w-4 h-4" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {saved ? 'Saved!' : 'Save Changes'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2 overflow-x-auto">
        {Object.entries(settingCategories).map(([key, category]) => {
          const Icon = category.icon
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                activeTab === key
                  ? 'bg-blue-500 text-white'
                  : 'text-slate-400 hover:bg-slate-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {category.label}
            </button>
          )
        })}
        <button
          onClick={() => setActiveTab('announcements')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
            activeTab === 'announcements'
              ? 'bg-blue-500 text-white'
              : 'text-slate-400 hover:bg-slate-700'
          }`}
        >
          <Megaphone className="w-4 h-4" />
          Announcements
        </button>
      </div>

      {/* Settings Content */}
      {activeTab !== 'announcements' && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl">
          {loadingSettings ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                <p className="text-slate-400">{error}</p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-slate-700">
              {settingCategories[activeTab]?.keys.map(key => {
                const setting = settings[key]
                if (!setting) return null

                return (
                  <div key={key} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <h3 className="text-white font-medium">{setting.label || key}</h3>
                      {setting.description && (
                        <p className="text-slate-400 text-sm mt-1">{setting.description}</p>
                      )}
                    </div>
                    {hasPermission('settings.edit') ? (
                      renderSettingInput(setting)
                    ) : (
                      <span className="text-slate-300">
                        {setting.type === 'boolean' ? (setting.value ? 'Enabled' : 'Disabled') : setting.value}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Announcements Tab */}
      {activeTab === 'announcements' && (
        <>
          {/* Add Button */}
          {hasPermission('settings.edit') && (
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setEditingAnnouncement(null)
                  setShowAnnouncementModal(true)
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Announcement
              </button>
            </div>
          )}

          {/* Announcements List */}
          <div className="space-y-4">
            {loadingAnnouncements ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                  <p className="text-slate-400">{error}</p>
                </div>
              </div>
            ) : announcements.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                No announcements yet
              </div>
            ) : (
              announcements.map((announcement) => (
                <motion.div
                  key={announcement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-slate-800 border rounded-xl p-5 ${
                    announcement.type === 'warning' ? 'border-yellow-500/30' :
                    announcement.type === 'error' ? 'border-red-500/30' :
                    announcement.type === 'success' ? 'border-emerald-500/30' :
                    'border-slate-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Megaphone className={`w-5 h-5 ${
                          announcement.type === 'warning' ? 'text-yellow-400' :
                          announcement.type === 'error' ? 'text-red-400' :
                          announcement.type === 'success' ? 'text-emerald-400' :
                          'text-blue-400'
                        }`} />
                        <h3 className="text-lg font-semibold text-white">{announcement.title}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          announcement.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'
                        }`}>
                          {announcement.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-slate-400 mb-3">{announcement.message}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(announcement.start_date)} - {announcement.end_date ? formatDate(announcement.end_date) : 'No end date'}
                        </span>
                        <span className="capitalize px-2 py-0.5 bg-slate-700 rounded">
                          {announcement.type}
                        </span>
                      </div>
                    </div>
                    {hasPermission('settings.edit') && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingAnnouncement(announcement)
                            setShowAnnouncementModal(true)
                          }}
                          className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-4 h-4 text-slate-400" />
                        </button>
                        <button
                          onClick={() => handleDeleteAnnouncement(announcement.id)}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </>
      )}

      {/* Announcement Modal */}
      <AnimatePresence>
        {showAnnouncementModal && (
          <AnnouncementFormModal
            announcement={editingAnnouncement}
            onClose={() => {
              setShowAnnouncementModal(false)
              setEditingAnnouncement(null)
            }}
            onSave={handleSaveAnnouncement}
            processing={saving}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Announcement Form Modal Component
function AnnouncementFormModal({ announcement, onClose, onSave, processing }) {
  const [formData, setFormData] = useState({
    title: announcement?.title || '',
    message: announcement?.message || '',
    type: announcement?.type || 'info',
    start_date: announcement?.start_date?.slice(0, 10) || new Date().toISOString().slice(0, 10),
    end_date: announcement?.end_date?.slice(0, 10) || '',
    is_active: announcement?.is_active ?? true
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      ...formData,
      id: announcement?.id
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {announcement ? 'Edit Announcement' : 'New Announcement'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                placeholder="Announcement title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Message</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500 resize-none"
                placeholder="Announcement message..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Type</label>
              <div className="flex gap-2">
                {['info', 'warning', 'error', 'success'].map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setFormData({ ...formData, type })}
                    className={`flex-1 px-3 py-2 rounded-lg transition-colors capitalize ${
                      formData.type === type
                        ? type === 'info' ? 'bg-blue-500 text-white' :
                          type === 'warning' ? 'bg-yellow-500 text-white' :
                          type === 'error' ? 'bg-red-500 text-white' :
                          'bg-emerald-500 text-white'
                        : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">End Date (optional)</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 bg-slate-900/50 border border-slate-600 rounded"
              />
              <label htmlFor="is_active" className="text-slate-300">Active (visible to users)</label>
            </div>
          </div>

          <div className="p-6 border-t border-slate-700 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Announcement
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}
